import clsx from 'clsx';
import { KLEED_FASEN, FASE_STAP_LABEL, type KleedFase, type RepairItemSummary } from '../../lib/reparatie';

interface Props {
  item: RepairItemSummary;
}

/**
 * De fysieke reis van één kleed als zeven stappen: gedaan (gevuld), nu (ring),
 * komt nog (grijs). Alleen lezen — de tijdlijn is een status, geen knoppenrij.
 * Datums onder de gedane stappen komen uit de stempels en de ritten.
 */
export function ReparatieKleedTijdlijn({ item }: Props) {
  const huidige = item.fase ?? 'bij_klant';
  const huidigeIdx = KLEED_FASEN.indexOf(huidige);

  const datumVoor = (fase: KleedFase): string | null => {
    switch (fase) {
      case 'heen_gepland':
        return kort(item.geplandeRitHeen?.datum);
      case 'bij_reparateur':
        return kort(item.reparatieAfgeleverdOp);
      case 'klaar':
        return kort(item.reparatieKlaarOp);
      case 'terug_gepland':
        return kort(item.geplandeRitTerug?.datum);
      case 'terug_in_wasserij':
        return kort(item.reparatieOpgehaaldOp);
      default:
        return null;
    }
  };

  return (
    <ol className="flex items-start gap-0 w-full" aria-label="Fase van dit kleed">
      {KLEED_FASEN.map((fase, idx) => {
        const gedaan = idx < huidigeIdx;
        const nu = idx === huidigeIdx;
        const datum = datumVoor(fase);
        return (
          <li key={fase} className="flex-1 min-w-0 flex flex-col items-center relative">
            {idx > 0 && (
              <span
                className={clsx(
                  'absolute top-[5px] right-1/2 w-full h-0.5 -translate-y-1/2',
                  gedaan || nu ? 'bg-indigo-300' : 'bg-slate-200'
                )}
                aria-hidden
              />
            )}
            <span
              className={clsx(
                'relative z-10 w-2.5 h-2.5 rounded-full border-2',
                gedaan && 'bg-indigo-500 border-indigo-500',
                nu && 'bg-white border-indigo-600 ring-2 ring-indigo-200',
                !gedaan && !nu && 'bg-white border-slate-300'
              )}
              aria-hidden
            />
            <span
              className={clsx(
                'mt-1 text-[10px] leading-tight text-center truncate max-w-full px-0.5',
                nu ? 'text-indigo-700 font-medium' : gedaan ? 'text-slate-600' : 'text-slate-400'
              )}
              title={FASE_STAP_LABEL[fase]}
            >
              {FASE_STAP_LABEL[fase]}
            </span>
            {datum && (gedaan || nu) && (
              <span className="text-[10px] text-slate-400 leading-tight">{datum}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function kort(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}
